import { action, makeObservable, observable } from "mobx";
import { fetchProtectedResource, isNumeric } from "../../utilities";
import { message } from "antd";
import { isArray } from "lodash";
import BaseService from "../base/BaseService";
import {
	QUOTE_NOTES_URL,
	QUOTE_PATCH_URL,
	QUOTE_RENEW_URL,
	QUOTE_RENEWALS_URL,
} from "../../configs/api-paths";

/**
 * MobX service containing quote renewals.
 */
class RenewalsService extends BaseService {
	/**
	 * Renewals about all quotes
	 * @type {[]}
	 */
	@observable
	renewalsData = [];

	/**
	 * Is the service loading data.
	 * @type {boolean}
	 */
	@observable
	loading = false;

	/**
	 * Renewals table pagination object.
	 * @type {{}}
	 */
	@observable
	pagination = {};

	/**
	 * Renewals table sorting object.
	 * @type {{}}
	 */
	@observable
	sorter = {
		field: "renewalDate",
		order: "descend",
	};

	@observable
	search = {};

	constructor(props) {
		super(props);
		makeObservable(this);
	}

	@action
	setRenewalsData = (data) => {
		this.renewalsData = data;
	};

	@action
	setLoading = (loading) => {
		this.loading = loading;
	};

	@action
	setPagination = (pagination) => {
		this.pagination = pagination;
	};

	@action
	setSorter = (sorter) => {
		this.sorter = sorter;
	};

	@action
	setSearch = (search) => {
		this.search = search;
	};

	/**
	 * @returns {Promise<void>}
	 */
	loadRenewalsData = async () => {
		this.setLoading(true);
		let hideLoadingMessage = message.loading("Loading...", 0);

		try {
			let field = this.sorter.field;
			const order = this.sorter.order;

			const currentPage = this.pagination.current;
			const pageSize = this.pagination.pageSize;

			let url = `${QUOTE_RENEWALS_URL}?`;

			if (field && order) {
				if (field === "first_name") {
					field = "organisation.firstName";
				}

				if (field === "last_name") {
					field = "organisation.lastName";
				}

				if (field === "phone") {
					field = "organisation.phone";
				}

				if (field === "address1") {
					field = "organisation.address1";
				}

				if (field === "address2") {
					field = "organisation.address2";
				}

				if (field === "city") {
					field = "organisation.city";
				}

				if (field === "post_code") {
					field = "organisation.postcode";
				}

				url += `sort=${field},${order === "ascend" ? "asc" : "desc"}&`;
			}

			if (isNumeric(currentPage) && isNumeric(pageSize)) {
				url += `page=${currentPage - 1}&size=${pageSize}&`;
			}

			if (isArray(this.search.fields)) {
				url += `searchFields=${this.search.fields.join(",")}&`;
				url += `searchValue=${this.search.value}&`;
			}

			const result = await fetchProtectedResource(url);
			hideLoadingMessage();
			const pagedResults = await result.json();

			this.setPagination({
				...this.pagination,
				total: pagedResults.totalElements,
			});
			this.setRenewalsData(pagedResults.content);
		} catch (error) {
			console.log(error);

			hideLoadingMessage();
			message.error(
				`There was error while trying to fetch renewals data from the server. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};

	updateQuoteNotRenewingReason = async (notRenewingReason, id) => {
		this.setLoading(true);

		try {
			let url = `${QUOTE_PATCH_URL}${id}`;

			await fetchProtectedResource(url, {
				method: "PATCH",
				body: { notRenewingReason },
			});
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to save not renewing reason to the server. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};

	renewQuote = async (id) => {
		this.setLoading(true);
		try {
			let url = `${QUOTE_RENEW_URL.replace("{id}", id)}`;

			await fetchProtectedResource(url, {
				method: "PUT",
			});
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to renew this quote. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};

	saveQuoteNotes = async (id, notes) => {
		this.setLoading(true);

		try {
			let url = `${QUOTE_NOTES_URL.replace("{id}", id)}`;

			await fetchProtectedResource(url, {
				method: "PUT",
				body: {
					notes,
				},
			});
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to save notes for this quote. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};
}

export default RenewalsService;
